/**
 * Bridge Pattern - Data Provider Factory
 * 
 * This factory creates and provides the appropriate data provider based on configuration.
 * It's the central place to switch between LocalStorage and Firestore implementations.
 * 
 * Usage:
 * 1. Set the provider type in configuration
 * 2. Get the provider instance using getDataProvider()
 * 3. All data operations go through this provider
 * 
 * To switch from LocalStorage to Firestore:
 * 1. Change DATA_PROVIDER_TYPE to 'firestore'
 * 2. Implement all methods in FirestoreDataProvider
 * 3. That's it! No changes needed anywhere else
 */

import { IDataProvider } from './IDataProvider';
import { LocalStorageDataProvider } from './LocalStorageDataProvider';
import { FirestoreDataProvider } from './FirestoreDataProvider';

/**
 * Available data provider types
 */
export type DataProviderType = 'localStorage' | 'firestore';

/**
 * Configuration for the data provider
 * 
 */
const DATA_PROVIDER_TYPE: DataProviderType = 'firestore';

/**
 * Enable this to override provider type from URL parameter
 * Example: ?dataProvider=firestore
 */
const ALLOW_URL_OVERRIDE = true;

/**
 * Singleton instance of the data provider
 */
let providerInstance: IDataProvider | null = null;

/**
 * Get the provider type from configuration or URL override
 */
function getProviderType(): DataProviderType {
  if (ALLOW_URL_OVERRIDE && typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const urlProvider = urlParams.get('dataProvider') as DataProviderType;
    if (urlProvider === 'localStorage' || urlProvider === 'firestore') {
      console.log(`[DataProviderFactory] Using URL override: ${urlProvider}`);
      return urlProvider;
    }
  }
  
  return DATA_PROVIDER_TYPE;
}

/**
 * Create a new data provider instance based on type
 */
function createProvider(type: DataProviderType): IDataProvider {
  console.log(`[DataProviderFactory] Creating provider: ${type}`);
  
  switch (type) {
    case 'localStorage':
      return new LocalStorageDataProvider();
    
    case 'firestore':
      console.log('[DataProviderFactory] Using Firestore provider');
      return new FirestoreDataProvider();
    
    default:
      console.error(`[DataProviderFactory] Unknown provider type: ${type}, falling back to localStorage`);
      return new LocalStorageDataProvider();
  }
}

/**
 * Get the current data provider instance (singleton)
 * This is the main function to use throughout the application
 */
export function getDataProvider(): IDataProvider {
  if (!providerInstance) {
    const type = getProviderType();
    providerInstance = createProvider(type);
    console.log(`[DataProviderFactory] Initialized ${providerInstance.getProviderName()} provider`);
  }
  
  return providerInstance;
}

/**
 * Reset the provider instance (useful for testing or switching providers at runtime)
 */
export function resetDataProvider(): void {
  console.log('[DataProviderFactory] Resetting provider instance');
  providerInstance = null;
}

/**
 * Switch to a different provider type at runtime
 * WARNING: This will reset the current provider instance
 */
export function switchDataProvider(type: DataProviderType): IDataProvider {
  console.log(`[DataProviderFactory] Switching to ${type} provider`);
  resetDataProvider();
  providerInstance = createProvider(type);
  return providerInstance;
}

/**
 * Get information about the current provider
 */
export function getProviderInfo(): {
  type: DataProviderType;
  name: string;
  configured: DataProviderType;
  allowUrlOverride: boolean;
} {
  const provider = getDataProvider();
  return {
    type: getProviderType(),
    name: provider.getProviderName(),
    configured: DATA_PROVIDER_TYPE,
    allowUrlOverride: ALLOW_URL_OVERRIDE,
  };
}

/**
 * Check if the current provider is ready
 */
export async function isProviderReady(): Promise<boolean> {
  const provider = getDataProvider();
  return provider.isReady();
}
